import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { globalVariables } from "../../global/GlobalVariables";
import {
  deleteData,
  findGetParameter,
  isUsingWS,
  postData
} from "../../components/utilities/UtilityFunctions";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";

import * as Yup from "yup";
import { Formik } from "formik";
import GenericPicker from "../../components/utilities/GenericPicker";
import { userDisplayName } from "../events/components/EventFunctions";
import { handlePagingAll } from "../../components/api/APIUtilities";
import { calculateTeamResults } from "./GameRouter";
import {
  BCPCircleCheck,
  BCPEllipsisVertical,
  BCPGear,
  BCPLockKeyhole,
  BCPLockKeyholeOpen,
  BCPXmark
} from "../../components/icons/bcp/BCPIcons";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import SwapScorecardModal from "./components/SwapScorecardModal";
import StyledMenu from "../../components/layout/StyledMenu";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { isModalEmbed } from "../../global/AppOverlay";
import { useTranslation } from "react-i18next";

const jwt = findGetParameter("jwt");
const embed = findGetParameter("embed") === "true";

export default function TeamPairing(props) {
  const {
    teamPairing,
    setTeamPairing,
    appRoute,
    modal,
    setRefreshPairings,
    setSwapTableModal,
    setP
  } = props;

  const navigate = useNavigate();

  const modalEmbed = isModalEmbed();

  const authContext = useContext(AuthContext);

  const { t } = useTranslation("translation", {
    keyPrefix: "misc.pairings"
  });
  const { t: tGeneral } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [scorecard, setScorecard] = useState(null);

  const [isPaired, setIsPaired] = useState(false);
  const [editPairings, setEditPairings] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [resultsModal, setResultsModal] = useState(null);

  const [modifierModal, setModifierModal] = useState(null);

  const [pinModal, setPinModal] = useState(false);
  const [afterPinBody, setAfterPinBody] = useState(null);
  const [afterPinFunction, setAfterPinFunction] = useState(null);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const theme = useTheme();

  const useWebSockets = isUsingWS(authContext);

  const getPairings = async () => {
    var apiPairingsData = await handlePagingAll("/pairings", {
      pairingType: "Pairing",
      teamPairingId: teamPairing.id,
      "expand[]": ["player1", "player1Game", "player2", "player2Game"]
    });
    if (apiPairingsData?.length) {
      apiPairingsData.sort((a, b) => {
        return (a.table || 0) - (b.table || 0);
      });
    }
    return apiPairingsData;
  };

  useEffect(() => {
    const check = async () => {
      try {
        if (teamPairing) {
          setIsPaired(teamPairing.isPaired);
          setEditPairings(!teamPairing.isPaired);
          if (teamPairing.scorecard) {
            setScorecard(teamPairing.scorecard);
          }
        }
      } catch (err) {
        postNotification("error", err);
      }
      if (loading) setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, []);

  const savePairings = async (values) => {
    var body = {
      pairingType: "TeamPairing",
      pairings: []
    };
    const jwt = findGetParameter("jwt");
    if (jwt && jwt !== "") body.jwt = jwt;

    if (values.teamPlayer1Pin !== undefined) {
      body.teamPlayer1Pin = values.teamPlayer1Pin;
    }
    if (values.teamPlayer2Pin !== undefined) {
      body.teamPlayer2Pin = values.teamPlayer2Pin;
    }
    for (let pairing of values?.pairings) {
      var pairingObj = {
        table: pairing.table || 0
      };
      if (pairing.id) {
        pairingObj.id = pairing.id;
      }
      if (pairing.player1Id) {
        pairingObj.player1Id = pairing.player1Id;
      }
      if (pairing.player2Id) {
        pairingObj.player2Id = pairing.player2Id;
      }
      body.pairings.push(pairingObj);
    }

    setProcessing(true);

    try {
      var apiPairing = await postData(
        `/pairings/${teamPairing?.id}/saveTeamPairings`,
        {
          body: body
        }
      );

      postNotification("success", t("pairingsUpdated"));

      if (teamPairing.event?.adepticonTeamEvent) {
        navigate(
          teamPairing.event?.isTO
            ? `/organize/event/${teamPairing.event.id}?active_tab=pairings${
                teamPairing.event.currentRound
                  ? `&round=${teamPairing.event.currentRound}`
                  : ""
              }`
            : `/event/${teamPairing.event.id}?active_tab=pairings${
                teamPairing.event.currentRound
                  ? `&round=${teamPairing.event.currentRound}`
                  : ""
              }`
        );
        return;
      }
      if (modal) {
        if (useWebSockets) {
          setP(null);
        } else {
          window.location.assign(
            teamPairing.event?.isTO
              ? `/organize/event/${teamPairing.event.id}?active_tab=pairings${
                  teamPairing.event.currentRound
                    ? `&round=${teamPairing.event.currentRound}`
                    : ""
                }`
              : teamPairing.event
                ? `/event/${teamPairing.event.id}?active_tab=pairings${
                    teamPairing.event.currentRound
                      ? `&round=${teamPairing.event.currentRound}`
                      : ""
                  }`
                : "/games"
          );
        }
      }

      var apiPairings = await getPairings();
      var newTeamPairing = {
        ...teamPairing,
        ...apiPairing,
        pairings: apiPairings
      };
      let resultDict = calculateTeamResults(newTeamPairing, scorecard);
      newTeamPairing.results = resultDict;
      setTeamPairing(newTeamPairing);
      setEditPairings(!editPairings);
      setIsPaired(true);
      setAfterPinFunction(null);
      setAfterPinBody(null);
      setPinModal(false);
    } catch (err) {
      console.error(err);
      postNotification("error", err);
    }

    setProcessing(false);
  };

  const submitScores = async (values) => {
    var body = {
      pairingType: "TeamPairing",
      ...values
    };
    const jwt = findGetParameter("jwt");
    if (jwt && jwt !== "") body.jwt = jwt;

    if (values.teamPlayer1Pin !== undefined) {
      body.teamPlayer1Pin = values.teamPlayer1Pin;
    }
    if (values.teamPlayer2Pin !== undefined) {
      body.teamPlayer2Pin = values.teamPlayer2Pin;
    }

    setProcessing(true);

    try {
      await postData(`/pairings/${teamPairing?.id}/submitScores`, {
        body: body
      });

      setAfterPinFunction(null);
      setAfterPinBody(null);
      setPinModal(false);

      if (embed === true || appRoute || modalEmbed) {
        setIsSubmitted(true);
      } else {
        postNotification("success", t("teamPairingSubmitted"));

        if (modal) {
          if (useWebSockets) {
            setP(null);
          } else {
            window.location.assign(
              teamPairing.event?.isTO
                ? `/organize/event/${teamPairing.event.id}?active_tab=pairings${
                    teamPairing.event.currentRound
                      ? `&round=${teamPairing.event.currentRound}`
                      : ""
                  }`
                : teamPairing.event
                  ? `/event/${teamPairing.event.id}?active_tab=pairings${
                      teamPairing.event.currentRound
                        ? `&round=${teamPairing.event.currentRound}`
                        : ""
                    }`
                  : "/games"
            );
          }
        } else {
          navigate(
            teamPairing.event?.isTO
              ? `/organize/event/${teamPairing.event.id}?active_tab=pairings${
                  teamPairing.event.currentRound
                    ? `&round=${teamPairing.event.currentRound}`
                    : ""
                }`
              : teamPairing.event
                ? `/event/${teamPairing.event.id}?active_tab=pairings${
                    teamPairing.event.currentRound
                      ? `&round=${teamPairing.event.currentRound}`
                      : ""
                  }`
                : "/games"
          );
        }
      }
    } catch (err) {
      postNotification("error", err);
    }

    setProcessing(false);
  };

  const deleteModifier = async (modifierId) => {
    setProcessing(true);

    try {
      var apiPairing = await deleteData(
        `/pairings/${teamPairing?.id}/modifier/${modifierId}`,
        {
          body: { pairingType: "TeamPairing" }
        }
      );

      postNotification("success", t("modifierDeleted"));

      setTeamPairing({
        ...teamPairing,
        ...apiPairing
      });
    } catch (err) {
      postNotification("error", err);
    }

    setProcessing(false);
  };
  const saveModifier = async (values) => {
    if (!values.comments) {
      throw new Error(t("modifierCommentsRequired"));
    }
    if (!values.gameData) {
      throw new Error(t("modifierGameDataRequired"));
    }
    var body = {
      pairingType: "TeamPairing",
      ...values
    };

    setProcessing(true);

    try {
      var apiPairing = await postData(
        `/pairings/${teamPairing?.id}/saveModifier`,
        {
          body: body
        }
      );

      postNotification(
        "success",
        values.id ? t("modifierUpdated") : t("modifierAdded")
      );

      setTeamPairing({
        ...teamPairing,
        ...apiPairing
      });
    } catch (err) {
      postNotification("error", err);
    }

    setProcessing(false);
  };

  return (
    <>
      {(loading || processing) && <ModalLoadingFull />}
      {teamPairing && (
        <Container sx={{ pt: modal ? 0 : 2, pb: modal ? 0 : 2 }} maxWidth="lg">
          {isSubmitted ? (
            <Grid container>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <BCPCircleCheck
                  color={theme?.palette?.success.main}
                  sx={{ fontSize: 200 }}
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <Typography variant="h5">
                  {t("submittedMessage", {
                    siteTitle:
                      globalVariables().site?.title || "Best Coast Pairings"
                  })}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container sx={{ gap: 2 }}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 2
                  }}
                >
                  <Grid container>
                    <Grid
                      item
                      sx={{
                        width: "auto",
                        flex: 1
                      }}
                    >
                      <Typography variant="h4" gutterBottom>
                        {t("teamPairingScorecard")}
                        {teamPairing?.event?.name
                          ? `: ${teamPairing?.event?.name}`
                          : ""}
                      </Typography>
                    </Grid>

                    {!modalEmbed &&
                      (teamPairing?.event?.isTO ||
                        authContext?.authState?.identity?.isAdmin ||
                        modal) && (
                        <Grid item textAlign="right">
                          <IconButton
                            aria-label="settings"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOptionsAnchorEl(e.currentTarget);
                            }}
                          >
                            <BCPGear />
                          </IconButton>
                        </Grid>
                      )}

                    {!embed &&
                      !modal &&
                      !appRoute &&
                      !modalEmbed &&
                      teamPairing?.eventId && (
                        <Grid item textAlign="right" sx={{ width: "50px" }}>
                          <IconButton
                            aria-label="cancel"
                            color="error"
                            href={
                              teamPairing.event?.isTO
                                ? `/organize/event/${teamPairing?.eventId}?active_tab=pairings`
                                : `/event/${teamPairing?.eventId}?active_tab=pairings`
                            }
                          >
                            <BCPXmark />
                          </IconButton>
                        </Grid>
                      )}
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    p: 2
                  }}
                >
                  <Formik
                    initialValues={teamPairing}
                    enableReinitialize
                    onSubmit={async () => {
                      return;
                    }}
                  >
                    {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      touched,
                      values,
                      setFieldValue
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Grid
                          container
                          spacing={1}
                          sx={{
                            pb: 1,
                            mb: 2,
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                          }}
                        >
                          <Grid item xs={5} sx={{ overflowX: "hidden" }}>
                            {teamPairing?.teamPlayer1?.name || "--------"}
                          </Grid>
                          <Grid
                            item
                            xs={2}
                            sx={{
                              textAlign: "center"
                            }}
                          >
                            {t("table")}
                          </Grid>
                          <Grid
                            item
                            xs={5}
                            sx={{
                              textAlign: "right",
                              overflowX: "hidden"
                            }}
                          >
                            {teamPairing?.teamPlayer2?.name || "--------"}
                          </Grid>
                        </Grid>

                        {!loading && (
                          <>
                            {values?.pairings?.length > 0 &&
                              values.pairings.map((pairing, index) => {
                                if (!editPairings || !teamPairing.showButtons) {
                                  return (
                                    <PlayerRow
                                      index={index}
                                      row={pairing}
                                      modal={modal}
                                      teamPairing={teamPairing}
                                      event={teamPairing?.event}
                                      appRoute={appRoute}
                                    />
                                  );
                                }

                                return (
                                  <PairingRow
                                    index={index}
                                    values={values}
                                    modal={modal}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    setFieldValue={setFieldValue}
                                    teamPairing={teamPairing}
                                    touched={touched}
                                    errors={errors}
                                    appRoute={appRoute}
                                    t={t}
                                  />
                                );
                              })}

                            {teamPairing?.modifiers?.length > 0 && (
                              <>
                                {teamPairing.modifiers.map(
                                  (modifier, index) => {
                                    return (
                                      <ModifierRow
                                        key={index}
                                        teamPairing={teamPairing}
                                        scorecard={scorecard}
                                        modifier={modifier}
                                        setModifierModal={setModifierModal}
                                      />
                                    );
                                  }
                                )}
                              </>
                            )}

                            {teamPairing.showButtons && (
                              <Grid
                                container
                                spacing={1}
                                sx={{
                                  pb: 1,
                                  mb: 2,
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                                }}
                              >
                                <Grid item xs={!editPairings ? 12 : 6}>
                                  <Button
                                    type="button"
                                    variant={
                                      !isPaired && !editPairings
                                        ? "contained"
                                        : "outlined"
                                    }
                                    color={
                                      !isPaired && !editPairings
                                        ? "success"
                                        : "warning"
                                    }
                                    fullWidth
                                    onClick={() => {
                                      setEditPairings(!editPairings);
                                    }}
                                  >
                                    {editPairings
                                      ? t("cancel")
                                      : t("editPairings")}
                                  </Button>
                                </Grid>

                                {editPairings && (
                                  <Grid item xs={6}>
                                    <Button
                                      type="button"
                                      variant="contained"
                                      color="success"
                                      fullWidth
                                      onClick={async () => {
                                        if (
                                          teamPairing?.event?.isTO ||
                                          teamPairing?.event
                                            ?.passwordlessScoring
                                        ) {
                                          await savePairings(values);
                                        } else {
                                          setAfterPinFunction("savePairings");
                                          setAfterPinBody(values);
                                          setPinModal(true);
                                        }
                                      }}
                                    >
                                      {t("save", { keyPrefix: "buttons" })}
                                    </Button>
                                  </Grid>
                                )}
                              </Grid>
                            )}

                            {(teamPairing.event?.isTO ||
                              !teamPairing.event?.adepticonTeamEvent) && (
                              <>
                                {isPaired &&
                                  !editPairings &&
                                  scorecard?.template?.rows?.filter(
                                    (row) => row.summary === true
                                  )?.length > 0 && (
                                    <>
                                      {scorecard.template.rows.map(
                                        (row, index) => {
                                          if (!row.summary) {
                                            return <></>;
                                          }

                                          return (
                                            <Grid
                                              key={index}
                                              container
                                              spacing={1}
                                            >
                                              {row.cols.map((col, colIndex) => {
                                                return (
                                                  <Grid
                                                    key={index}
                                                    item
                                                    xs={12 / row.cols.length}
                                                  >
                                                    <TextField
                                                      label={col.label}
                                                      value={
                                                        teamPairing?.results?.[
                                                          col.id
                                                        ] || 0
                                                      }
                                                      variant="outlined"
                                                      fullWidth
                                                      margin="normal"
                                                      disabled
                                                      InputProps={{
                                                        inputProps: {
                                                          style: {
                                                            textAlign: "center"
                                                          }
                                                        }
                                                      }}
                                                    />
                                                  </Grid>
                                                );
                                              })}
                                            </Grid>
                                          );
                                        }
                                      )}
                                    </>
                                  )}

                                {isPaired &&
                                  !editPairings &&
                                  teamPairing.event?.isTO && (
                                    <Grid
                                      container
                                      spacing={1}
                                      sx={{
                                        mb: 1
                                      }}
                                    >
                                      <Grid item xs={12}>
                                        <Button
                                          type="button"
                                          variant="outlined"
                                          color="warning"
                                          fullWidth
                                          onClick={() => {
                                            setModifierModal({});
                                          }}
                                        >
                                          {t("addModifier")}
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  )}

                                {teamPairing.showButtons &&
                                  isPaired &&
                                  !editPairings && (
                                    <Grid
                                      container
                                      sx={{
                                        pb: 1,
                                        mb: 2,
                                        borderBottom:
                                          "1px solid rgba(0, 0, 0, 0.12)"
                                      }}
                                    >
                                      <Grid item xs={12}>
                                        <Button
                                          type="button"
                                          variant="contained"
                                          color="success"
                                          fullWidth
                                          disabled={!isPaired}
                                          onClick={async () => {
                                            setProcessing(true);
                                            var body = { ...values };
                                            try {
                                              var apiGetPairings =
                                                await getPairings();
                                              body.pairings = apiGetPairings;
                                            } catch (err) {
                                              postNotification("error", err);
                                              setProcessing(false);
                                              return;
                                            }
                                            setProcessing(false);
                                            if (!body.pairings?.length) {
                                              postNotification(
                                                "error",
                                                t("teamPairingHasNoRecords")
                                              );
                                              return;
                                            }
                                            if (
                                              body.pairings.filter(
                                                (el) => !el.isDone
                                              ).length
                                            ) {
                                              postNotification(
                                                "error",
                                                t(
                                                  "teamPairingHasUnsubmittedRecords"
                                                )
                                              );
                                              return;
                                            }
                                            setResultsModal(body);
                                          }}
                                        >
                                          {t("submitScores")}
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  )}
                              </>
                            )}
                          </>
                        )}
                      </form>
                    )}
                  </Formik>
                </Grid>
              </Grid>

              <ResultsModal
                teamPairing={teamPairing}
                scorecard={scorecard}
                results={resultsModal}
                submitScores={submitScores}
                setResults={setResultsModal}
                setAfterPinBody={setAfterPinBody}
                setAfterPinFunction={setAfterPinFunction}
                setPinModal={setPinModal}
                t={t}
              />
              <PinsModal
                teamPairing={teamPairing}
                afterPinBody={afterPinBody}
                setAfterPinBody={setAfterPinBody}
                afterPinFunction={afterPinFunction}
                setAfterPinFunction={setAfterPinFunction}
                savePairings={savePairings}
                submitScores={submitScores}
                pinModal={pinModal}
                setPinModal={setPinModal}
                t={t}
              />

              <OptionsMenu
                anchorEl={optionsAnchorEl}
                setAnchorEl={setOptionsAnchorEl}
                pairing={teamPairing}
                authContext={authContext}
                event={teamPairing?.event}
                setSwapTableModal={setSwapTableModal}
                setLoading={setLoading}
                setRefreshPairings={setRefreshPairings}
                modal={modal}
                setP={setP}
                t={t}
                tGeneral={tGeneral}
              />

              {teamPairing?.event?.isTO && (
                <ModifierModal
                  teamPairing={teamPairing}
                  scorecard={scorecard}
                  saveModifier={saveModifier}
                  deleteModifier={deleteModifier}
                  modifierModal={modifierModal}
                  setModifierModal={setModifierModal}
                  t={t}
                />
              )}
            </>
          )}
        </Container>
      )}
    </>
  );
}

function OptionsMenu(props) {
  const {
    anchorEl,
    setAnchorEl,
    authContext,
    event,
    pairing,
    setSwapTableModal,
    setLoading,
    setRefreshPairings,
    modal,
    setP,
    t,
    tGeneral
  } = props;

  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const useWebSockets = isUsingWS(authContext);

  const [loading] = useState(false);
  const [sCMenuOpen, setSCMenuOpen] = useState(false);

  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button"
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {modal && (
          <>
            <MenuItem
              disableRipple
              onClick={() => {
                navigate(
                  `/game/${
                    pairing?.pairingType
                      ? pairing.pairingType.toLowerCase()
                      : "pairing"
                  }/${pairing.id}`
                );
              }}
            >
              {t("expandScorecard")}
            </MenuItem>
            <Divider sx={{ mt: 1, mb: 1 }} />
          </>
        )}
        {(pairing?.event?.isTO ||
          authContext?.authState?.identity?.isAdmin) && (
          <>
            {modal && (
              <>
                {pairing?.pairingType?.toLowerCase() === "pairing" &&
                  Boolean(
                    event.scorecardId &&
                      event.gameSystem?.scorecardId &&
                      event.scorecardId !== event.gameSystem.scorecardId
                  ) && (
                    <MenuItem
                      component="a"
                      href={`/game/${
                        pairing?.pairingType
                          ? pairing.pairingType.toLowerCase()
                          : "pairing"
                      }/${pairing?.id}?useGSD=true`}
                      disableRipple
                    >
                      {getEnvironmentVariable("LABEL_GAMESYSTEM")}{" "}
                      {t("scorecard")}
                    </MenuItem>
                  )}
                <MenuItem
                  onClick={(e) => {
                    setSwapTableModal(true);
                    setAnchorEl(null);
                  }}
                  disableRipple
                >
                  {t("changeTable")}
                </MenuItem>
                <MenuItem
                  onClick={async (e) => {
                    try {
                      await confirm({
                        title: `${t("confirmClearScores")}`,
                        description: t("confirmClearScoresDescription"),
                        dialogProps: {
                          sx: {
                            zIndex: 99999
                          }
                        }
                      });
                    } catch (err) {
                      return;
                    }
                    try {
                      setLoading(true);
                      await postData(`/pairings/${pairing.id}/clearScores`, {
                        body: {
                          pairingType: pairing?.pairingType || "Pairing"
                        }
                      });
                      setRefreshPairings(true);
                      postNotification("success", t("scoresCleared"));
                      setLoading(false);
                      setAnchorEl(null);
                    } catch (err) {
                      postNotification("error", err);
                      setLoading(false);
                    }
                  }}
                  disableRipple
                >
                  {t("clearScores")}
                </MenuItem>

                <MenuItem
                  onClick={async (e) => {
                    setLoading(true);

                    var metricDict = {
                      gameResult: "gameResult",
                      gamePoints: "gamePoints"
                    };
                    var body = {
                      isTO: true,
                      pairingType: pairing?.pairingType || "Pairing",
                      isDone: true,
                      metaData: {},
                      gameData: {
                        metrics: []
                      }
                    };
                    if (authContext?.authState?.identity?.userId) {
                      body.userId = authContext?.authState?.identity?.userId;
                    }
                    if (pairing?.metaData) {
                      Object.keys(pairing.metaData).forEach((key) => {
                        if (
                          pairing.metaData[key] !== "" &&
                          (typeof pairing.metaData[key] === "number" ||
                            !isNaN(Number(pairing.metaData[key])))
                        ) {
                          body.metaData[key] = "0";
                        } else {
                          body.metaData[key] = "";
                        }
                      });
                    }

                    let playerNo = 1;
                    if (pairing.seatedPlayers?.length) {
                      for (let seatedPlayer of pairing.seatedPlayers) {
                        let player = `player${playerNo}`;
                        body[`player${playerNo}Id`] = seatedPlayer.playerId;
                        body.gameData[player] = {
                          gameResult: 0,
                          gamePoints: 0
                        };
                        if (pairing.games?.[seatedPlayer.playerId]) {
                          for (let key of Object.keys(
                            pairing.games[seatedPlayer.playerId]
                          )) {
                            metricDict[key] = key;
                            body.gameData[player][key] = 0;
                          }
                        }
                        playerNo++;
                      }
                    } else {
                      while (
                        pairing &&
                        pairing[
                          `${body.pairingType === "TeamPairing" ? "teamPlayer" : "player"}${playerNo}`
                        ]
                      ) {
                        let player = `player${playerNo}`;
                        body.gameData[player] = {
                          gameResult: 0,
                          gamePoints: 0
                        };
                        if (pairing[player]?.game) {
                          for (let key of Object.keys(pairing[player].game)) {
                            metricDict[key] = key;
                            body.gameData[player][key] = 0;
                          }
                        }
                        playerNo++;
                      }
                    }

                    Object.keys(metricDict).forEach((key) => {
                      body.gameData.metrics.push(key);
                    });

                    try {
                      setLoading(true);
                      await postData(`/pairings/${pairing.id}/submitScores`, {
                        body: body
                      });
                      setAnchorEl(null);

                      if (!useWebSockets) setRefreshPairings(true);

                      postNotification("success", t("pairingUpdated"));
                      setLoading(false);
                      setP(null);
                    } catch (err) {
                      postNotification("error", err);
                      setLoading(false);
                    }
                  }}
                  disableRipple
                >
                  {pairing?.pairingType?.toLowerCase() === "teampairing"
                    ? t("giveBothTeamsLosses")
                    : pairing?.pairingType?.toLowerCase() === "seatedpairing"
                      ? t("giveAllPlayersLosses")
                      : t("giveBothAllPlayersLosses")}
                </MenuItem>

                <MenuItem
                  onClick={async () => {
                    try {
                      await confirm({
                        title: t("confirmDeletePairing"),
                        description: t("deletePairingConfirmation"),
                        dialogProps: {
                          sx: {
                            zIndex: 99999
                          }
                        }
                      });
                    } catch (err) {
                      return;
                    }
                    try {
                      setLoading(true);
                      await deleteData(`/pairings/${pairing.id}`, {
                        body: {
                          pairingType: pairing?.pairingType || "Pairing"
                        }
                      });
                      setRefreshPairings(true);
                      postNotification("success", t("pairingDeleted"));
                      setLoading(false);
                      setAnchorEl(null);
                    } catch (err) {
                      postNotification("error", err);
                      setLoading(false);
                    }
                  }}
                  disableRipple
                >
                  {pairing?.pairingType?.toLowerCase() === "teampairing"
                    ? t("deleteTeamPairing")
                    : t("deletePairing")}
                </MenuItem>

                <Divider sx={{ mt: 1, mb: 1 }} />
              </>
            )}

            <MenuItem
              disableRipple
              onClick={() => {
                postNotification(
                  "warning",
                  tGeneral("notifications.warnings.comingSoon")
                );
              }}
            >
              {t("swapScores")}
            </MenuItem>

            <MenuItem
              disableRipple
              onClick={(e) => {
                setSCMenuOpen(true);
              }}
            >
              {t("changeScorecard")}
            </MenuItem>
          </>
        )}
      </StyledMenu>
      {loading && <ModalLoadingFull />}

      <SwapScorecardModal
        sCMenuOpen={sCMenuOpen}
        setSCMenuOpen={setSCMenuOpen}
        authContext={authContext}
        event={event}
        pairing={pairing}
      />
    </>
  );
}

const PairingRow = (props) => {
  const {
    index,
    readOnly,
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    teamPairing,
    t
  } = props;
  const confirm = useConfirm();
  return (
    <Grid
      key={index}
      container
      spacing={1}
      sx={{
        pb: 1,
        mb: 2,
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
      }}
    >
      <Grid item xs={5}>
        <GenericPicker
          name={`pairings[${index}].player1Id`}
          value={values?.pairings?.[index]?.player1Id}
          handleChange={async (e) => {
            try {
              if (e?.target?.value !== values?.pairings?.[index]?.player1Id) {
                if (
                  values?.pairings?.[index]?.isDone ||
                  values?.pairings?.[index]?.isActiveUser ||
                  values?.pairings?.[index]?.metaData
                ) {
                  await confirm({
                    title: "Confirm Clear Scores",
                    description: t("teamPairingReset", {
                      status: values?.pairings?.[index]?.isDone
                        ? t("beenSubmitted")
                        : values?.pairings?.[index]?.isActiveUser
                          ? t("activeUsers")
                          : t("pairingData")
                    }),
                    dialogProps: {
                      sx: {
                        zIndex: 99999
                      }
                    },
                    confirmationText: t("continue"),
                    cancellationText: t("cancel")
                  });
                }
              }
            } catch (err) {
              return;
            }
            handleChange(e);
          }}
          formControlSX={{
            mt: 0,
            mb: 0,
            "& .MuiInputBase-root": {
              minHeight: "60px",
              maxHeight: "60px",
              "& .MuiSelect-select": {
                padding: "4px",
                paddingRight: "16px"
              }
            }
          }}
          list={
            teamPairing?.teamPlayer1?.players
              ? [
                  ...teamPairing.teamPlayer1.players.map((player) => {
                    return {
                      value: player.id,
                      label: (
                        <Box sx={{ display: "block" }}>
                          <Typography variant="body1">
                            {userDisplayName(
                              player?.firstName || player?.user?.firstName,
                              player?.lastName || player?.user?.lastName
                            )}
                          </Typography>
                          {(player.army?.name || player.armyName) && (
                            <Typography variant="caption">
                              {player.army?.name || player.armyName}
                            </Typography>
                          )}
                        </Box>
                      )
                    };
                  }),
                  {
                    value: "",
                    label: "--------"
                  }
                ]
              : [
                  {
                    value: "",
                    label: "--------"
                  }
                ]
          }
          inclAny={false}
          disabled={readOnly}
        />
      </Grid>

      <Grid item xs={2}>
        <TextField
          name={`pairings[${index}].table`}
          value={values?.pairings?.[index]?.table}
          type="number"
          variant="outlined"
          fullWidth
          margin="normal"
          onBlur={(e) => {
            var newVal =
              e.target.value.trim() !== ""
                ? parseInt(e.target.value)
                : e.target.value;
            setFieldValue(`pairings[${index}].table`, newVal);
          }}
          onChange={(e) => {
            var newVal =
              e.target.value.trim() !== ""
                ? parseInt(e.target.value)
                : e.target.value;
            setFieldValue(`pairings[${index}].table`, newVal);
          }}
          error={Boolean(
            touched?.pairings?.[index]?.table &&
              errors?.pairings?.[index]?.table
          )}
          helperText={
            touched?.pairings?.[index]?.table &&
            errors?.pairings?.[index]?.table
          }
          disabled={readOnly}
          InputProps={{
            inputProps: { min: 0, style: { textAlign: "center" } }
          }}
          sx={{
            mt: 0,
            mb: 0,
            "& .MuiInputBase-root": {
              minHeight: "60px",
              maxHeight: "60px",
              "& .MuiOutlinedInput-input": {
                padding: "4px"
              }
            }
          }}
        />
      </Grid>

      <Grid
        item
        xs={5}
        sx={{
          textAlign: "right"
        }}
      >
        <GenericPicker
          name={`pairings[${index}].player2Id`}
          value={values?.pairings?.[index]?.player2Id}
          handleChange={async (e) => {
            try {
              if (e?.target?.value !== values?.pairings?.[index]?.player1Id) {
                if (
                  values?.pairings?.[index]?.isDone ||
                  values?.pairings?.[index]?.isActiveUser ||
                  values?.pairings?.[index]?.metaData
                ) {
                  await confirm({
                    title: "Confirm Clear Scores",
                    description: t("teamPairingReset", {
                      status: values?.pairings?.[index]?.isDone
                        ? t("beenSubmitted")
                        : values?.pairings?.[index]?.isActiveUser
                          ? t("activeUsers")
                          : t("pairingData")
                    }),
                    dialogProps: {
                      sx: {
                        zIndex: 99999
                      }
                    },
                    confirmationText: t("continue"),
                    cancellationText: t("cancel")
                  });
                }
              }
            } catch (err) {
              return;
            }
            handleChange(e);
          }}
          formControlSX={{
            mt: 0,
            mb: 0,
            "& .MuiInputBase-root": {
              minHeight: "60px",
              maxHeight: "60px",
              "& .MuiSelect-select": {
                padding: "4px",
                paddingRight: "16px"
              }
            }
          }}
          list={
            teamPairing?.teamPlayer2?.players
              ? [
                  ...teamPairing.teamPlayer2.players.map((player) => {
                    return {
                      value: player.id,
                      label: (
                        <Box sx={{ display: "block" }}>
                          <Typography variant="body1">
                            {userDisplayName(
                              player?.firstName || player?.user?.firstName,
                              player?.lastName || player?.user?.lastName
                            )}
                          </Typography>
                          {(player.army?.name || player.armyName) && (
                            <Typography variant="caption">
                              {player.army?.name || player.armyName}
                            </Typography>
                          )}
                        </Box>
                      )
                    };
                  }),
                  {
                    value: "",
                    label: "--------"
                  }
                ]
              : [
                  {
                    value: "",
                    label: "--------"
                  }
                ]
          }
          inclAny={false}
          disabled={readOnly}
        />
      </Grid>
    </Grid>
  );
};

const PlayerRow = (props) => {
  const {
    appRoute,
    organize,
    row,
    setPairing,
    setPairingPopUp,
    setPlayer,
    setOptionsAnchorEl2,
    team
  } = props;
  if (organize) {
    return (
      <Grid
        item
        xs={12}
        onClick={(e) => {
          setPairing(row);
          setPairingPopUp(row);
        }}
        sx={{
          p: 1,
          mb: team ? null : 1,
          border: team ? null : "1px solid rgba(0, 0, 0, 0.12)",
          borderBottom: team ? "1px solid rgba(0, 0, 0, 0.12)" : null,
          borderRadius: team ? null : "4px",
          boxShadow: team ? null : 1,
          "&:hover": {
            cursor: "pointer"
          }
        }}
      >
        <Grid container spacing={1}>
          <Grid item sx={{ maxWidth: "50px" }}>
            <IconButton
              sx={{
                color: "info.contrastText",
                backgroundColor: "info.main",
                "&:hover": { backgroundColor: "info.dark" }
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPairing(row);
                setPlayer(row["player1"]);
                setOptionsAnchorEl2(e.currentTarget);
              }}
            >
              <BCPEllipsisVertical />
            </IconButton>
          </Grid>
          <Grid item sx={{ width: "auto", flex: 1 }}>
            <PlayerResult
              organize={organize}
              pairing={row}
              playerNo="player1"
              appRoute={appRoute}
            />
          </Grid>
          <Grid item sx={{ width: "16.66%", textAlign: "center" }}>
            <Typography>{row.table}</Typography>
            {row.isDone && (
              <Chip
                label="SUBMITTED"
                color="success"
                sx={{
                  height: ["28px !important", null],
                  "& .MuiChip-label": {
                    fontSize: ["10px", "small"],
                    pl: ["8px !important", null],
                    pr: ["8px !important", null]
                  }
                }}
              />
            )}
            {Boolean(
              !row.isDone &&
                (row.player1?.game?.gameResult !== undefined ||
                  row.player2?.game?.gameResult !== undefined)
            ) && (
              <Chip
                label="LIVE"
                color="info"
                sx={{
                  height: ["28px !important", null],
                  "& .MuiChip-label": {
                    fontSize: ["10px", "small"],
                    pl: ["8px !important", null],
                    pr: ["8px !important", null]
                  }
                }}
              />
            )}
          </Grid>
          <Grid
            item
            sx={{
              width: "auto",
              textAlign: "right",
              flex: 1
            }}
          >
            <PlayerResult
              organize={organize}
              pairing={row}
              playerNo="player2"
              appRoute={appRoute}
            />
          </Grid>
          <Grid
            item
            sx={{
              maxWidth: "50px",
              textAlign: "right"
            }}
          >
            <IconButton
              sx={{
                color: "info.contrastText",
                backgroundColor: "info.main",
                "&:hover": { backgroundColor: "info.dark" }
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPairing(row);
                setPlayer(row["player2"]);
                setOptionsAnchorEl2(e.currentTarget);
              }}
            >
              <BCPEllipsisVertical />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid
        item
        xs={12}
        sx={{
          p: 1,
          mb: team ? null : 1,
          border: team ? null : "1px solid rgba(0, 0, 0, 0.12)",
          borderBottom: team ? "1px solid rgba(0, 0, 0, 0.12)" : null,
          borderRadius: team ? null : "4px",
          boxShadow: team ? null : 1
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs sx={{ width: "41.666667%" }}>
            <PlayerResult
              pairing={row}
              playerNo="player1"
              appRoute={appRoute}
            />
          </Grid>
          <Grid item xs sx={{ width: "16.66%", textAlign: "center" }}>
            <Link
              href={
                row.id
                  ? `/game/${
                      row?.pairingType
                        ? row.pairingType.toLowerCase()
                        : "pairing"
                    }/${row.id}?${embed || appRoute ? "&embed=true" : ""}${
                      jwt && jwt !== "" ? `&jwt=${jwt}` : ""
                    }`
                  : "#"
              }
              underline="none"
            >
              <Typography>{row.table}</Typography>
              {row.isDone && (
                <Chip
                  label="SUBMITTED"
                  color="success"
                  sx={{
                    height: ["28px !important", null],
                    "& .MuiChip-label": {
                      fontSize: ["10px", "small"],
                      pl: ["8px !important", null],
                      pr: ["8px !important", null]
                    }
                  }}
                />
              )}
              {Boolean(
                !row.isDone &&
                  (row.player1?.game?.gameResult !== undefined ||
                    row.player2?.game?.gameResult !== undefined)
              ) && (
                <Chip
                  label="LIVE"
                  color="info"
                  sx={{
                    height: ["28px !important", null],
                    "& .MuiChip-label": {
                      fontSize: ["10px", "small"],
                      pl: ["8px !important", null],
                      pr: ["8px !important", null]
                    }
                  }}
                />
              )}
            </Link>
          </Grid>
          <Grid
            item
            xs
            sx={{
              width: "41.666667%",
              textAlign: "right"
            }}
          >
            <PlayerResult
              pairing={row}
              playerNo="player2"
              appRoute={appRoute}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }
};

const ModifierRow = (props) => {
  const { teamPairing, scorecard, modifier, setModifierModal } = props;
  const [rows, setRows] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    var newRows = [];
    if (modifier?.gameData) {
      for (let row of scorecard?.template?.rows) {
        if (row.cols.length) {
          var newRow = undefined;
          for (let col of row.cols) {
            if (col.bcpupdate && col.bcpfield) {
              var fields = col.bcpfield.split(".");
              if (fields.length === 2) {
                var field = modifier.gameData[fields[0]]?.[fields[1]];
                if (field !== undefined) {
                  if (fields[0] === "player1Game") {
                    if (!newRow) newRow = {};
                    newRow.left = {
                      label: col.label,
                      value: field,
                      comments: modifier.comments
                    };
                  } else if (fields[0] === "player2Game") {
                    if (!newRow) newRow = {};
                    newRow.right = {
                      label: col.label,
                      value: field,
                      comments: modifier.comments
                    };
                  }
                }
              }
            }
          }
          if (newRow) {
            newRows.push(newRow);
          }
        }
      }
    }
    setRows(newRows);
    // eslint-disable-next-line
  }, [modifier]);

  return (
    <>
      {rows?.length > 0 &&
        rows.map((row, index) => (
          <Grid
            key={index}
            container
            sx={{
              p: 1,
              mb: 1,
              backgroundColor:
                theme?.palette?.mode === "dark" ? null : "rgb(255, 244, 229)",
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderBottom: null,
              borderRadius: "4px",
              boxShadow: 1,
              "&:hover": teamPairing?.event?.isTO
                ? {
                    cursor: "pointer"
                  }
                : null
            }}
            onClick={() => {
              if (teamPairing?.event?.isTO) {
                setModifierModal(modifier);
              }
            }}
          >
            <Grid item xs={6}>
              {row.left && (
                <>
                  {row.left.comments ? (
                    <Tooltip title={row.left.comments}>
                      <Typography variant="body1">{`${row.left.label}: ${row.left.value}`}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body1">
                      {row.left.label
                        ? `${row.left.label}: ${row.left.value}`
                        : ""}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={6} textAlign="right">
              {row.right && (
                <>
                  {row.right.comments ? (
                    <Tooltip title={row.right.comments}>
                      <Typography variant="body1">{`${row.right.label}: ${row.right.value}`}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body1">
                      {row.right.label
                        ? `${row.right.label}: ${row.right.value}`
                        : ""}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        ))}
    </>
  );
};

const PlayerResult = (props) => {
  const { playerNo, pairing, organize, appRoute } = props;
  const player = pairing[playerNo];
  const game = pairing[`${playerNo.replace("teamPlayer", "player")}Game`];

  const { t } = useTranslation("translation", {
    keyPrefix: "misc.pairings"
  });

  var result = "";
  if (game?.gameResult >= 2) {
    result = t("win");
  } else if (game?.gameResult === 1) {
    result = t("draw");
  } else {
    result = t("loss");
  }
  var color = result.toLowerCase();

  if (playerNo.startsWith("teamPlayer")) {
    color = "#fff";
  }

  const name = player?.name
    ? player.name
    : player?.id
      ? `${player?.firstName}\n${player?.lastName}`
      : "--------";

  const SolforgeDisplay = () => {
    return (
      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{
          "& .MuiTypography-root": { fontSize: "16px", m: 0 },
          "& .MuiTypography-root:not(:first-child)": { fontSize: "14px", m: 0 }
        }}
      >
        {playerNo === "player1" && (
          <Grid item sx={{ maxWidth: "40px" }}>
            <Avatar
              alt={name}
              src={player.profileImageUrl}
              sx={{ width: 30, height: 30 }}
            />
          </Grid>
        )}
        <Grid item sx={{ flex: 1 }}>
          <PlayerResultDisplay />
        </Grid>
        {playerNo === "player2" && (
          <Grid item sx={{ maxWidth: "40px", textAlign: "right" }}>
            <Avatar
              alt={name}
              src={player.profileImageUrl}
              sx={{ width: 30, height: 30 }}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  const PlayerResultDisplay = () => {
    return (
      <>
        {name?.split("\n")?.map((n, i) => (
          <Typography key={i}>{n}</Typography>
        ))}

        {game?.gameResult !== undefined && (
          <Typography sx={{ color: `${color}.main` }}>{`${result}${
            game?.gamePoints !== undefined ? `: ${game.gamePoints}` : ""
          }`}</Typography>
        )}
      </>
    );
  };

  if (organize || playerNo.startsWith("teamPlayer")) {
    return getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
      <SolforgeDisplay />
    ) : (
      <PlayerResultDisplay />
    );
  } else {
    return (
      <Link
        href={
          pairing.id
            ? `/game/${
                pairing?.pairingType
                  ? pairing.pairingType.toLowerCase()
                  : "pairing"
              }/${pairing.id}?${embed || appRoute ? "&embed=true" : ""}${
                jwt && jwt !== "" ? `&jwt=${jwt}` : ""
              }`
            : "#"
        }
        underline="none"
      >
        {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
          <SolforgeDisplay />
        ) : (
          <PlayerResultDisplay />
        )}
      </Link>
    );
  }
};

function ResultsModal(props) {
  const {
    teamPairing,
    scorecard,
    results,
    submitScores,
    setResults,
    setPinModal,
    setAfterPinFunction,
    setAfterPinBody,
    t
  } = props;
  const [locked, setLocked] = useState(true);

  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    if (!results) {
      setInitialValues(null);
      return;
    }

    let resultDict = calculateTeamResults(teamPairing, scorecard);

    setInitialValues(resultDict);
    // eslint-disable-next-line
  }, [results]);

  return (
    <Modal
      disableAutoFocus
      open={Boolean(results)}
      onClose={() => {
        setResults(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      sx={{ zIndex: Number.MAX_SAFE_INTEGER }}
    >
      {Boolean(results) ? (
        <>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={(values) => {
              var body = {
                isDone: true,
                metaData: values,
                teamPlayer1Id: teamPairing?.teamPlayer1Id,
                teamPlayer2Id: teamPairing?.teamPlayer2Id,
                gameData: {
                  metrics: {},
                  player1: {},
                  player2: {}
                }
              };
              for (let row of scorecard?.template?.rows) {
                if (row.cols) {
                  for (let col of row.cols) {
                    if (col.bcpupdate && values[col.id] !== undefined) {
                      if (col.bcpfield) {
                        let fields = col.bcpfield
                          .replace("Game.", ".")
                          .split(".");
                        let field = body.gameData;
                        for (let i = 0; i < fields.length; i++) {
                          if (i < fields.length - 1) {
                            if (field[fields[i]] === undefined) {
                              field[fields[i]] = {};
                            }
                            field = field[fields[i]];
                          } else {
                            body.gameData.metrics[fields[i]] = true;
                            field[fields[i]] = values[col.id];
                          }
                        }
                      }
                    }
                  }
                }
              }
              body.gameData.metrics = Object.keys(body.gameData.metrics);
              if (
                teamPairing?.event?.isTO ||
                teamPairing?.event?.passwordlessScoring
              ) {
                submitScores(body);
              } else {
                setAfterPinBody(body);
                setAfterPinFunction("submitScores");
                setPinModal(true);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: ["95vw", "85vw", 600],
                    maxHeight: "95vh",
                    bgcolor: "background.paper",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 24,
                    p: 4
                  }}
                >
                  <IconButton
                    aria-label="cancel"
                    color="error"
                    sx={{ position: "absolute", top: "4px", right: "4px" }}
                    onClick={() => {
                      setResults(null);
                    }}
                  >
                    <BCPXmark />
                  </IconButton>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs>
                      <Typography variant="h4" component="h2">
                        {t("teamScores")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs={6}>
                      {teamPairing?.teamPlayer1?.name || "--------"}
                    </Grid>

                    <Grid
                      item
                      xs={6}
                      sx={{
                        textAlign: "right"
                      }}
                    >
                      {teamPairing?.teamPlayer2?.name || "--------"}
                    </Grid>
                  </Grid>

                  {scorecard?.template?.rows?.map((row, index) => {
                    if (row.hidden) {
                      return <></>;
                    }
                    return (
                      <Grid
                        key={index}
                        container
                        spacing={1}
                        sx={{
                          pb: 1,
                          mb: 2,
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                        }}
                      >
                        {row.cols.map((col, colIndex) => {
                          var inputProps = {
                            style: { textAlign: "center" }
                          };
                          if (col.min !== undefined) {
                            inputProps.min = col.min;
                          }
                          if (col.max !== undefined) {
                            inputProps.max = col.max;
                          }
                          return (
                            <Grid key={colIndex} item xs={12 / row.cols.length}>
                              <TextField
                                name={col.id}
                                label={col.label}
                                value={values?.[col.id]}
                                type={col.subType}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched?.[col.id] && errors?.[col.id]
                                )}
                                helperText={
                                  touched?.[col.id] && errors?.[col.id]
                                }
                                disabled={locked}
                                InputProps={{
                                  inputProps: inputProps
                                }}
                                sx={{
                                  mt: 0,
                                  mb: 0,
                                  "& .MuiInputBase-root": {
                                    minHeight: "60px",
                                    maxHeight: "60px",
                                    "& .MuiOutlinedInput-input": {
                                      padding: "4px"
                                    }
                                  }
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}

                  <Grid container spacing={1}>
                    {teamPairing?.event?.isTO && (
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          endIcon={
                            !locked ? (
                              <BCPLockKeyholeOpen outlined />
                            ) : (
                              <BCPLockKeyhole outlined />
                            )
                          }
                          onClick={() => {
                            setLocked(!locked);
                          }}
                        >
                          {!locked ? t("unlocked") : t("locked")}
                        </Button>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={teamPairing?.event?.isTO ? 6 : 12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="success"
                        fullWidth
                      >
                        {t("submitResults")}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            )}
          </Formik>
        </>
      ) : (
        <></>
      )}
    </Modal>
  );
}

function PinsModal(props) {
  const {
    teamPairing,
    pinModal,
    setPinModal,
    afterPinBody,
    setAfterPinBody,
    afterPinFunction,
    setAfterPinFunction,
    savePairings,
    submitScores,
    t
  } = props;

  const closePins = () => {
    setAfterPinFunction(null);
    setAfterPinBody(null);
    setPinModal(false);
  };

  return (
    <Modal
      disableAutoFocus
      open={pinModal}
      onClose={closePins}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      {pinModal === true ? (
        <>
          <Formik
            initialValues={{
              teamPlayer1Pin: "",
              teamPlayer2Pin: ""
            }}
            onSubmit={async (values) => {
              if (afterPinFunction === "savePairings") {
                await savePairings({
                  ...afterPinBody,
                  ...values
                });
              } else if (afterPinFunction === "submitScores") {
                await submitScores({
                  ...afterPinBody,
                  ...values
                });
              }
              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: ["95vw", "85vw", 600],
                    maxHeight: "95vh",
                    bgcolor: "background.paper",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 24,
                    p: 4
                  }}
                >
                  <IconButton
                    aria-label="cancel"
                    color="error"
                    sx={{ position: "absolute", top: "4px", right: "4px" }}
                    onClick={closePins}
                  >
                    <BCPXmark />
                  </IconButton>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs>
                      <Typography variant="h4" component="h2">
                        {t("enterSelfScorePins")}
                      </Typography>
                      <Typography variant="caption">
                        {t("selfScorePinsDescription")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs={12}>
                      <TextField
                        name="teamPlayer1Pin"
                        label={`${
                          teamPairing?.teamPlayer1?.name || "--------"
                        } ${t("selfScorePin")}`}
                        value={values.teamPlayer1Pin}
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched?.teamPlayer1Pin && errors?.teamPlayer1Pin
                        )}
                        helperText={
                          touched?.teamPlayer1Pin && errors?.teamPlayer1Pin
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        name="teamPlayer2Pin"
                        label={`${
                          teamPairing?.teamPlayer2?.name || "--------"
                        } ${t("selfScorePin")}`}
                        value={values.teamPlayer2Pin}
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(
                          touched?.teamPlayer2Pin && errors?.teamPlayer2Pin
                        )}
                        helperText={
                          touched?.teamPlayer2Pin && errors?.teamPlayer2Pin
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={closePins}
                      >
                        {t("cancel")}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        type="submit"
                      >
                        {t("submit", { keyPrefix: "buttons" })}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            )}
          </Formik>
        </>
      ) : (
        <></>
      )}
    </Modal>
  );
}

function ModifierModal(props) {
  const {
    teamPairing,
    scorecard,
    deleteModifier,
    saveModifier,
    modifierModal,
    setModifierModal,
    t
  } = props;
  const confirm = useConfirm();
  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    if (!modifierModal) {
      setInitialValues(null);
      return;
    }

    let newValues = {
      comments: "",
      gameData: {},
      ...modifierModal
    };

    for (let row of scorecard?.template?.rows) {
      if (row.cols) {
        for (let col of row.cols) {
          if (col.id) {
            if (col.bcpfield && col.bcpupdate) {
              let fields = col.bcpfield.split(".");
              if (fields.length === 2) {
                if (newValues.gameData[fields[0]] === undefined) {
                  newValues.gameData[fields[0]] = {};
                }
                newValues.gameData[fields[0]][fields[1]] =
                  modifierModal?.gameData?.[fields[0]]?.[fields[1]] || 0;
              }
            }
          }
        }
      }
    }
    setInitialValues(newValues);
    // eslint-disable-next-line
  }, [modifierModal]);

  return (
    <Modal
      disableAutoFocus
      open={Boolean(modifierModal)}
      onClose={() => {
        setModifierModal(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
      sx={{ zIndex: Number.MAX_SAFE_INTEGER }}
    >
      {Boolean(modifierModal) ? (
        <>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={Yup.object().shape({
              comments: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .required("Required")
            })}
            onSubmit={async (values) => {
              var body = {
                ...values
              };
              var keys1 = Object.keys(body.gameData);
              for (let key1 of keys1) {
                var keys2 = Object.keys(body.gameData[key1]);
                for (let key2 of keys2) {
                  if (!body.gameData[key1][key2]) {
                    delete body.gameData[key1][key2];
                  }
                }
                if (!Object.keys(body.gameData[key1]).length) {
                  delete body.gameData[key1];
                }
              }
              if (!Object.keys(body.gameData).length) {
                postNotification("error", t("modifyAtLeastOneMetric"));
                return;
              }

              try {
                await saveModifier(body);
                setModifierModal(null);
              } catch (err) {
                postNotification("error", err);
                return;
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: ["95vw", "85vw", 600],
                    maxHeight: "95vh",
                    overflowY: "scroll",
                    bgcolor: "background.paper",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 24,
                    p: 4
                  }}
                >
                  <IconButton
                    aria-label="cancel"
                    color="error"
                    sx={{ position: "absolute", top: "4px", right: "4px" }}
                    onClick={() => {
                      setModifierModal(null);
                    }}
                  >
                    <BCPXmark />
                  </IconButton>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs>
                      <Typography variant="h4" component="h2">
                        {t("teamModifier")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs={6}>
                      {teamPairing?.teamPlayer1?.name || "--------"}
                    </Grid>

                    <Grid
                      item
                      xs={6}
                      sx={{
                        textAlign: "right"
                      }}
                    >
                      {teamPairing?.teamPlayer2?.name || "--------"}
                    </Grid>
                  </Grid>

                  {scorecard?.template?.rows
                    ?.filter((row) => {
                      return (
                        !row.hidden &&
                        row.cols.filter((col) => {
                          return (
                            col.bcpupdate &&
                            col.bcpfield &&
                            col.bcpfield.split(".").length === 2
                          );
                        }).length
                      );
                    })
                    .map((row, index) => {
                      return (
                        <Grid
                          key={index}
                          container
                          spacing={1}
                          sx={{
                            pb: 1,
                            mb: 2,
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                          }}
                        >
                          {row.cols.map((col, colIndex) => {
                            var inputProps = {
                              style: { textAlign: "center" }
                            };
                            var fields = col.bcpfield.split(".");

                            return (
                              <Grid
                                key={colIndex}
                                item
                                xs={12 / row.cols.length}
                              >
                                <TextField
                                  name={`gameData.${fields[0]}.${fields[1]}`}
                                  label={col.label}
                                  value={
                                    values?.gameData?.[fields[0]]?.[fields[1]]
                                  }
                                  type={col.subType}
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  InputProps={{
                                    inputProps: inputProps
                                  }}
                                  sx={{
                                    mt: 0,
                                    mb: 0,
                                    "& .MuiInputBase-root": {
                                      minHeight: "60px",
                                      maxHeight: "60px",
                                      "& .MuiOutlinedInput-input": {
                                        padding: "4px"
                                      }
                                    }
                                  }}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      );
                    })}

                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <Grid item xs={12}>
                      <TextField
                        name="comments"
                        label={t("comments")}
                        value={values?.comments}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched?.comments && errors?.comments)}
                        helperText={touched?.comments && errors?.comments}
                        multiline
                        rows={3}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={async () => {
                          if (modifierModal?.id) {
                            try {
                              await confirm({
                                title: t("areYouSure"),
                                description: t(
                                  "areYouSureYouWantToDeleteThisModifier"
                                ),
                                dialogProps: {
                                  sx: {
                                    zIndex: 99999
                                  }
                                }
                              });

                              await deleteModifier(modifierModal.id);
                            } catch (err) {
                              return;
                            }
                          }

                          setModifierModal(null);
                        }}
                      >
                        {modifierModal?.id ? t("delete") : t("cancel")}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="success"
                        fullWidth
                      >
                        {t("saveModifier")}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            )}
          </Formik>
        </>
      ) : (
        <></>
      )}
    </Modal>
  );
}
